import type {
  AuthClientMetadata,
  AuthCreatePairingCredentialInput,
  AuthPairingCredentialResult,
  AuthRevokePairingLinkInput,
  AuthSessionId,
} from "@cmmd-forge/contracts";

import { resolvePrimaryEnvironmentHttpUrl } from "./target";
import { buildPrimaryFetchInit } from "./fetchInit";
import { readJsonResponse } from "./jsonResponse";

/**
 * The pairing-client surface: mint a one-time credential for another machine,
 * watch the client sessions it can create, and kill a link that leaked or was
 * mistyped. Kept apart from `auth.ts` so the credential-exchange machinery and
 * these three routes stay independently readable.
 */

async function readErrorMessage(response: Response, fallbackMessage: string): Promise<string> {
  const text = await response.text();
  return text || fallbackMessage;
}

export async function createServerPairingCredential(
  label?: string,
  role?: "owner" | "client",
): Promise<AuthPairingCredentialResult> {
  const trimmedLabel = label?.trim();
  const payload: AuthCreatePairingCredentialInput = {
    ...(trimmedLabel ? { label: trimmedLabel } : {}),
    ...(role ? { role } : {}),
  };
  const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/pairing-token");
  const response = await fetch(
    endpointUrl,
    buildPrimaryFetchInit({
      body: JSON.stringify(payload),
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
    }),
  );

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, `Failed to create pairing credential (${response.status}).`),
    );
  }

  return readJsonResponse<AuthPairingCredentialResult>(response, endpointUrl);
}

export async function listServerClientSessions(): Promise<
  ReadonlyArray<ServerClientSessionRecord>
> {
  const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/clients");
  const response = await fetch(endpointUrl, buildPrimaryFetchInit({ credentials: "include" }));

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, `Failed to load paired clients (${response.status}).`),
    );
  }

  return readJsonResponse<ReadonlyArray<ServerClientSessionRecord>>(response, endpointUrl);
}

/**
 * Revoke one minted pairing link by id. The hash-fragment credential the link
 * carries dies with it, so a link that never reached a machine (or reached one
 * the user no longer trusts) stops being a way into this server.
 */
export async function revokeServerPairingLink(id: string): Promise<boolean> {
  const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/pairing-links/revoke");
  const response = await fetch(
    endpointUrl,
    buildPrimaryFetchInit({
      body: JSON.stringify({ id } satisfies AuthRevokePairingLinkInput),
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
    }),
  );

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, `Failed to revoke the pairing link (${response.status}).`),
    );
  }

  const result = await readJsonResponse<{ revoked: boolean }>(response, endpointUrl);
  return result.revoked;
}

export interface ServerClientSessionRecord {
  readonly sessionId: AuthSessionId;
  readonly subject: string;
  readonly role: "owner" | "client";
  readonly method: "browser-session-cookie" | "bearer-session-token" | "proxy-asserted";
  readonly client: AuthClientMetadata;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly lastConnectedAt: string | null;
  readonly connected: boolean;
  readonly current: boolean;
}
